/**
 * ReportDetailView — header + indicator grid + dialog mount for a single
 * report. Extracted from `Reports.tsx` to keep that file under the
 * 500-line cap.
 */

import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  BarChart3,
  CalendarClock,
  ChevronDown,
  Copy,
  Calendar as CalendarIcon,
  Database,
  Download,
  Filter,
  Globe,
  History as HistoryIcon,
  Loader2,
  MoreVertical,
  Pencil,
  Plus,
  RefreshCw,
  Share2,
  Trash2,
} from 'lucide-react';
import { format } from 'date-fns';

import { IndicatorCard } from './charts';
import { AddIndicatorDialog } from './dialogs';
import { ReportsDialogs } from './ReportsDialogs';
import {
  type AnalyseCategory,
  type AnalyseDashboard,
  type DashboardIndicator,
  type Dataset,
  type ScheduledReportCreatePayload,
} from '../../lib/api/reporting';
import type { ExportFormat, NewIndicatorState } from './useReports';

interface ReportDetailViewProps {
  selectedReport: AnalyseDashboard;
  indicators: DashboardIndicator[];
  categories: AnalyseCategory[];
  datasets: Dataset[];
  loading: boolean;
  loadingIndicators: boolean;
  saving: boolean;
  isSupplier: boolean;

  // Add indicator dialog (v1)
  isAddIndicatorOpen: boolean;
  setIsAddIndicatorOpen: (open: boolean) => void;
  newIndicator: NewIndicatorState;
  setNewIndicator: (n: NewIndicatorState) => void;
  onCreateIndicator: () => void;

  // v2 dialog state + handlers
  isEditOpen: boolean;
  setIsEditOpen: (open: boolean) => void;
  reportToEdit: AnalyseDashboard | null;
  isDuplicateOpen: boolean;
  setIsDuplicateOpen: (open: boolean) => void;
  reportToDuplicate: AnalyseDashboard | null;
  isShareOpen: boolean;
  setIsShareOpen: (open: boolean) => void;
  reportToShare: AnalyseDashboard | null;
  isPublishOpen: boolean;
  setIsPublishOpen: (open: boolean) => void;
  reportToPublish: AnalyseDashboard | null;
  isRevisionsOpen: boolean;
  setIsRevisionsOpen: (open: boolean) => void;
  reportForRevisions: AnalyseDashboard | null;
  isDownloadLogsOpen: boolean;
  setIsDownloadLogsOpen: (open: boolean) => void;
  isFiscalCalendarOpen: boolean;
  setIsFiscalCalendarOpen: (open: boolean) => void;
  isScheduleOpen: boolean;
  setIsScheduleOpen: (open: boolean) => void;
  isExportOpen: boolean;
  setIsExportOpen: (open: boolean) => void;
  isFilterOpen: boolean;
  setIsFilterOpen: (open: boolean) => void;
  isEditIndicatorOpen: boolean;
  setIsEditIndicatorOpen: (open: boolean) => void;
  indicatorToEdit: DashboardIndicator | null;
  isDeleteReportOpen: boolean;
  setIsDeleteReportOpen: (open: boolean) => void;
  reportToDelete: AnalyseDashboard | null;

  // v2 actions
  openEditReport: (report: AnalyseDashboard) => void;
  openDuplicateReport: (report: AnalyseDashboard) => void;
  openShareReport: (report: AnalyseDashboard) => void;
  openPublishReport: (report: AnalyseDashboard) => void;
  openRevisionHistory: (report: AnalyseDashboard) => void;
  openDownloadLogs: () => void;
  openFiscalCalendar: () => void;
  openScheduleReport: () => void;
  openExportReport: () => void;
  openFilterDialog: () => void;
  openEditIndicator: (indicator: DashboardIndicator) => void;
  setReportToDelete: (report: AnalyseDashboard | null) => void;

  onBackToList: () => void;
  onRefresh: () => void;
  onCustomiseIndicator: (indicatorId: number, dashboardId?: number) => void;
  onEditReport: (payload: Partial<AnalyseDashboard>) => void;
  onDuplicateReport: (name: string) => void;
  onShareReport: (sharedWith: AnalyseDashboard['shared_with']) => Promise<void> | void;
  onScheduleReport: (payload: ScheduledReportCreatePayload) => void;
  onExportReport: (format: ExportFormat, applyFilters: boolean, indicatorIds?: number[]) => void;
  onAfterFilterChange: () => void;
  onEditIndicator: (payload: Partial<DashboardIndicator>) => void;
  onDeleteIndicator: (indicator: DashboardIndicator) => void;
  onOpenIndicatorBuilder: (indicator: DashboardIndicator) => void;
  onDeleteReport: () => void;
}

export function ReportDetailView(props: ReportDetailViewProps) {
  const {
    selectedReport,
    indicators,
    categories,
    datasets,
    loading,
    loadingIndicators,
    saving,
    isSupplier,
    isAddIndicatorOpen,
    setIsAddIndicatorOpen,
    newIndicator,
    setNewIndicator,
    onCreateIndicator,
    onBackToList,
    onRefresh,
    onCustomiseIndicator,
    openEditReport,
    openDuplicateReport,
    openShareReport,
    openPublishReport,
    openRevisionHistory,
    openDownloadLogs: _openDownloadLogs,
    openFiscalCalendar: _openFiscalCalendar,
    openScheduleReport,
    openExportReport,
    openFilterDialog,
    openEditIndicator,
    setReportToDelete,
    setIsDeleteReportOpen,
  } = props;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBackToList}>
            <ChevronDown className="h-4 w-4 mr-2 rotate-90" />
            Back to Reports
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{selectedReport.name}</h1>
            {selectedReport.description && (
              <p className="text-muted-foreground">{selectedReport.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={openFilterDialog}>
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
          <Button variant="outline" size="sm" onClick={openExportReport}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          {isSupplier && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openShareReport(selectedReport)}
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openPublishReport(selectedReport)}
              >
                <Globe className="h-4 w-4 mr-2" />
                Publish
              </Button>
              <Button variant="outline" size="sm" onClick={openScheduleReport}>
                <CalendarClock className="h-4 w-4 mr-2" />
                Schedule
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => openEditReport(selectedReport)}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit Report
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => openDuplicateReport(selectedReport)}>
                    <Copy className="h-4 w-4 mr-2" />
                    Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => openRevisionHistory(selectedReport)}>
                    <HistoryIcon className="h-4 w-4 mr-2" />
                    Revision history
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={() => {
                      setReportToDelete(selectedReport);
                      setIsDeleteReportOpen(true);
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button size="sm" onClick={() => setIsAddIndicatorOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Chart
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Report Info */}
      <Card>
        <CardContent className="py-3">
          <div className="flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Created:</span>
              <span>{format(new Date(selectedReport.created_at), 'dd MMM yyyy')}</span>
            </div>
            {selectedReport.category_name && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Category:</span>
                <Badge variant="outline">{selectedReport.category_name}</Badge>
              </div>
            )}
            {selectedReport.dataset_name && (
              <div className="flex items-center gap-2">
                <Database className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Dataset:</span>
                <Badge variant="outline">{selectedReport.dataset_name}</Badge>
              </div>
            )}
            <Badge variant={selectedReport.is_enabled ? 'default' : 'secondary'}>
              {selectedReport.is_enabled ? 'Active' : 'Disabled'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Indicators */}
      {loadingIndicators ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : indicators.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-medium mb-2">No charts configured</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Add charts and visualizations to this report.
            </p>
            {isSupplier && (
              <Button onClick={() => setIsAddIndicatorOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Chart
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {indicators.map((indicator) => (
            <IndicatorCard
              key={indicator.id}
              indicator={indicator}
              onEdit={isSupplier ? (ind) => openEditIndicator(ind) : undefined}
              onCustomise={isSupplier
                ? (ind) => onCustomiseIndicator(ind.id, selectedReport?.id)
                : undefined}
              onDelete={isSupplier ? props.onDeleteIndicator : undefined}
            />
          ))}
        </div>
      )}

      <AddIndicatorDialog
        open={isAddIndicatorOpen}
        onOpenChange={setIsAddIndicatorOpen}
        newIndicator={newIndicator}
        onIndicatorChange={setNewIndicator}
        saving={saving}
        onSubmit={onCreateIndicator}
        onCustomiseAfterCreate={(ind) => onCustomiseIndicator(ind.id, selectedReport?.id)}
      />

      <ReportsDialogs
        selectedReport={selectedReport}
        firstIndicatorId={indicators[0]?.id ?? null}
        categories={categories}
        datasets={datasets}
        saving={saving}
        isEditOpen={props.isEditOpen}
        setIsEditOpen={props.setIsEditOpen}
        reportToEdit={props.reportToEdit}
        onEditReport={props.onEditReport}
        isDuplicateOpen={props.isDuplicateOpen}
        setIsDuplicateOpen={props.setIsDuplicateOpen}
        reportToDuplicate={props.reportToDuplicate}
        onDuplicateReport={props.onDuplicateReport}
        isShareOpen={props.isShareOpen}
        setIsShareOpen={props.setIsShareOpen}
        reportToShare={props.reportToShare}
        onShareReport={props.onShareReport}
        isPublishOpen={props.isPublishOpen}
        setIsPublishOpen={props.setIsPublishOpen}
        reportToPublish={props.reportToPublish}
        isRevisionsOpen={props.isRevisionsOpen}
        setIsRevisionsOpen={props.setIsRevisionsOpen}
        reportForRevisions={props.reportForRevisions}
        isDownloadLogsOpen={props.isDownloadLogsOpen}
        setIsDownloadLogsOpen={props.setIsDownloadLogsOpen}
        isFiscalCalendarOpen={props.isFiscalCalendarOpen}
        setIsFiscalCalendarOpen={props.setIsFiscalCalendarOpen}
        isScheduleOpen={props.isScheduleOpen}
        setIsScheduleOpen={props.setIsScheduleOpen}
        onScheduleReport={props.onScheduleReport}
        isExportOpen={props.isExportOpen}
        setIsExportOpen={props.setIsExportOpen}
        onExportReport={props.onExportReport}
        isFilterOpen={props.isFilterOpen}
        setIsFilterOpen={props.setIsFilterOpen}
        onAfterFilterChange={props.onAfterFilterChange}
        isEditIndicatorOpen={props.isEditIndicatorOpen}
        setIsEditIndicatorOpen={props.setIsEditIndicatorOpen}
        indicatorToEdit={props.indicatorToEdit}
        onEditIndicator={props.onEditIndicator}
        onOpenIndicatorBuilder={props.onOpenIndicatorBuilder}
        isDeleteReportOpen={props.isDeleteReportOpen}
        setIsDeleteReportOpen={props.setIsDeleteReportOpen}
        reportToDelete={props.reportToDelete}
        onCancelDelete={() => setReportToDelete(null)}
        onConfirmDelete={props.onDeleteReport}
      />
    </div>
  );
}
